import * as React from "react";
import { observer } from "mobx-react-lite";
import { computed } from "mobx";
import { MetadataDefinition } from "../../administration/types/Metadata";
import { TaskPreviewModel } from "../types";
import {
    Button,
    DatePicker,
    Input,
    InputNumber,
    Select,
    Space,
    TimePicker,
    Tooltip,
} from "antd";
import { CheckOutlined, CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { TaskViewVisualStore } from "../stores";
import { AuthConsumer } from "../../authorization/AuthContext";
import { AppPermissions } from "../../authorization/Permissions";
import { hasPermission } from "../../authorization/components/HasPermission";
import { FULL_DATE, ONLY_DATE, Utils } from "../../common/misc/Utils";
import { Dayjs } from "dayjs";
import { useEffect, useRef, useMemo } from "react";
import MultiValueMetadataInput from "./MultiValueMetadataInput";

type Props = {
    store: TaskViewVisualStore;
    definition: MetadataDefinition;
    task: TaskPreviewModel;
};

const MultiValueMetadataEditor: React.FC<{
    definition: MetadataDefinition;
    value: unknown;
    onSave: (value: unknown[]) => void;
    onCancel: () => void;
}> = ({ definition, value, onSave, onCancel }) => {
    const [values, setValues] = React.useState<unknown[]>(
        Array.isArray(value)
            ? value
            : value === null || value === undefined
              ? []
              : [value],
    );

    return (
        <Space direction="vertical" style={{ width: "100%" }}>
            <MultiValueMetadataInput
                definition={definition}
                value={values}
                onChange={setValues}
                serializeDates
            />
            <Space>
                <Button
                    type="primary"
                    onClick={() =>
                        onSave(
                            values.filter(
                                (item) =>
                                    item !== null &&
                                    item !== undefined &&
                                    item !== "",
                            ),
                        )
                    }
                >
                    Save
                </Button>
                <Button onClick={onCancel}>Cancel</Button>
            </Space>
        </Space>
    );
};

const MetadataEditor: React.FC<Props> = ({ store, definition, task }) => {
    const [isEditing, setIsEditing] = React.useState<boolean>(false);
    const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const inputRef = useRef<any>(null);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const datePickerRef = useRef<any>(null);

    const metadataValue = useMemo(
        () =>
            computed(() => store.getMetadataValue(definition), {
                name: `MetadataValue_${definition.id}`,
            }),
        [store, definition],
    ).get();

    const displayValue = task.metadata
        ? Utils.getMetadataParseValue(metadataValue, definition)
        : "";

    const handleCopyClick = (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        Utils.copyTextToClipboard(String(displayValue ?? ""));
    };

    useEffect(() => {
        if (inputRef.current && isEditing) {
            inputRef.current.focus();
        }
    }, [inputRef, isEditing]);

    const handleEscapeClick = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Escape") {
            setIsEditing(false);
        }
    };

    useEffect(() => {
        const inputElement = datePickerRef.current?.nativeElement;

        if (inputElement && isEditing) {
            datePickerRef.current.focus();
            inputElement.addEventListener("keyup", handleEscapeClick);
        }

        // Clean up the event listener when the component is unmounted
        return () => {
            if (inputElement) {
                inputElement.removeEventListener("keyup", handleEscapeClick);
            }
        };
    }, [datePickerRef, isEditing]);

    const handleTextInputUpdate = () => {
        store
            .updateMetadataField(
                task.id,
                definition.name,
                inputRef.current?.input?.value || null,
            )
            .then(() => setIsEditing(false));
    };

    const handleInputUpdate = (val: unknown) => {
        store
            .updateMetadataField(
                task.id,
                definition.name,
                val === undefined ? null : val,
            )
            .then(() => setIsEditing(false));
    };

    const handleDateUpdate = (date: Dayjs | null) => {
        if (isUpdating) {
            return;
        }
        setIsUpdating(true);
        let ignoredTimezoneDate = null;
        if (date) {
            if (definition.format === ONLY_DATE) {
                ignoredTimezoneDate = Utils.removeLocalTimeZone(date);
            } else {
                ignoredTimezoneDate = Utils.ignoreLocalTimeZone(date);
            }
        }
        store
            .updateMetadataField(task.id, definition.name, ignoredTimezoneDate)
            .then(() => {
                setIsEditing(false);
                setIsUpdating(false);
            })
            .catch(() => {
                setIsUpdating(false);
            });
    };

    const widget = () => {
        if (definition.isMultiValue) {
            return (
                <MultiValueMetadataEditor
                    definition={definition}
                    value={metadataValue}
                    onSave={handleInputUpdate}
                    onCancel={() => setIsEditing(false)}
                />
            );
        }

        switch (definition.fieldType) {
            case "Number": {
                const { precision, separator } = definition.format
                    ? JSON.parse(definition.format)
                    : { precision: 2, separator: false };
                const stringifiedValue = metadataValue?.toString();
                const decimalCount = stringifiedValue?.includes(".")
                    ? (stringifiedValue.split(".")[1].length ?? 0)
                    : 0;
                const step = `0.${"0".repeat((decimalCount || precision || 2) - 1)}1`;

                const handleNumberSave = () => {
                    handleInputUpdate(
                        inputRef.current.value === ""
                            ? null
                            : Number(
                                  inputRef.current.value.replace(/(,*)/g, ""),
                              ),
                    );
                };

                return (
                    <InputNumber
                        ref={inputRef}
                        step={step}
                        formatter={
                            separator
                                ? (value) => {
                                      if (!value) return "";
                                      const [integerPart, decimalPart] =
                                          value.split(".");
                                      return `${integerPart.replace(
                                          /\B(?=(\d{3})+(?!\d))/g,
                                          ",",
                                      )}${decimalPart ? `.${decimalPart}` : ""}`;
                                  }
                                : undefined
                        }
                        parser={(value) => value!.replace(/\$\s?|(,*)/g, "")}
                        defaultValue={metadataValue}
                        style={{ width: "100%" }}
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        onKeyUp={(e: any) => {
                            if (e.key === "Escape") {
                                setIsEditing(false);
                            }
                            if (e.key === "Enter") {
                                handleInputUpdate(
                                    e.target.value === ""
                                        ? null
                                        : Number(
                                              e.target.value.replace(
                                                  /(,*)/g,
                                                  "",
                                              ),
                                          ),
                                );
                            }
                        }}
                        addonAfter={
                            <>
                                <CheckOutlined onClick={handleNumberSave} />
                                <CloseOutlined
                                    onClick={() => setIsEditing(false)}
                                    style={{ marginLeft: 10 }}
                                />
                            </>
                        }
                    />
                );
            }
            case "Text":
                return (definition.listValues?.length ?? 0) === 0 ? (
                    <Input
                        ref={inputRef}
                        onKeyUp={(e) => {
                            if (e.key === "Escape") {
                                setIsEditing(false);
                            }
                            if (e.key === "Enter") {
                                handleTextInputUpdate();
                            }
                        }}
                        defaultValue={metadataValue ?? ""}
                        addonAfter={
                            <>
                                <CheckOutlined
                                    onClick={handleTextInputUpdate}
                                />
                                <CloseOutlined
                                    onClick={() => setIsEditing(false)}
                                    style={{ marginLeft: 10 }}
                                />
                            </>
                        }
                    />
                ) : (
                    <Select
                        allowClear
                        value={Utils.getMetadataParseValue(
                            metadataValue,
                            definition,
                        )}
                        onChange={handleInputUpdate}
                        options={definition.listValues!}
                        onKeyUp={handleEscapeClick}
                    />
                );
            case "Boolean": {
                const { trueVal, falseVal } = definition.format
                    ? JSON.parse(definition.format)
                    : { trueVal: "true", falseVal: "false" };
                return (
                    <Select
                        allowClear
                        value={Utils.getMetadataParseValue(
                            metadataValue,
                            definition,
                        )}
                        onChange={handleInputUpdate}
                        options={[
                            { label: trueVal || "true", value: true },
                            { label: falseVal || "false", value: false },
                        ]}
                        onKeyUp={handleEscapeClick}
                    />
                );
            }
            case "DateTime":
                switch (definition.format) {
                    case ONLY_DATE:
                    case FULL_DATE:
                        return (
                            <DatePicker
                                ref={datePickerRef}
                                style={{ width: "100%" }}
                                suffixIcon={
                                    <i
                                        className="alpha-icon xs calendar-icon"
                                        style={{ margin: 0 }}
                                    />
                                }
                                format={Utils.getDateFormat(
                                    definition.format === FULL_DATE,
                                )}
                                showTime={definition.format === FULL_DATE}
                                onChange={handleDateUpdate}
                                value={Utils.formatDatePickerValue(
                                    metadataValue,
                                    undefined,
                                    true,
                                )}
                            />
                        );
                    default:
                        return (
                            <TimePicker
                                ref={datePickerRef}
                                style={{ width: "100%" }}
                                onChange={handleDateUpdate}
                            />
                        );
                }
            default:
                return <></>;
        }
    };

    return (
        <div className="meta-value">
            <AuthConsumer>
                {({ permissions, projectRoles }) => {
                    return isEditing ? (
                        widget()
                    ) : (
                        <div
                            className="meta-display"
                            onClick={() => {
                                if (
                                    !definition.calculation?.enabled &&
                                    (definition.isEditable ||
                                        !permissions ||
                                        hasPermission(
                                            permissions,
                                            AppPermissions.CanAccessAdministration,
                                            projectRoles,
                                            store.taskPreview!.projectId,
                                        ))
                                ) {
                                    setIsEditing(true);
                                }
                            }}
                        >
                            <span className="metadata-value-text">
                                {displayValue}
                            </span>
                            {displayValue !== undefined &&
                                displayValue !== null &&
                                displayValue !== "" && (
                                    <Tooltip title="Copy value">
                                        <Button
                                            type="link"
                                            size="small"
                                            className="metadata-copy-button"
                                            onClick={handleCopyClick}
                                            aria-label="Copy metadata value"
                                        >
                                            <CopyOutlined />
                                        </Button>
                                    </Tooltip>
                                )}
                        </div>
                    );
                }}
            </AuthConsumer>
        </div>
    );
};

export default observer(MetadataEditor);
